import {
  json,
  type HeadersFunction,
  type LoaderFunction,
  type MetaFunction,
} from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Grid } from '~/components/grid.tsx'
import { BlogSection } from '~/components/sections/blog-section.tsx'
import { HeroSection } from '~/components/sections/hero-section.tsx'
import { Paragraph } from '~/components/typography.tsx'
import { getSocialImageWithPreTitle } from '~/images.tsx'
import { type MdxListItem } from '~/types.ts'
import { getBlogRecommendations } from '~/utils/blog.server.ts'
import {
  getDisplayUrl,
  getUrl,
  reuseUsefulLoaderHeaders,
} from '~/utils/misc.tsx'
import { getSocialMetas } from '~/utils/seo.ts'
import { getServerTimeHeader } from '~/utils/timing.server.ts'
import { type RootLoaderType } from '~/root.tsx'
import { Experiences } from '~/components/experiences.tsx'
import { Education } from '~/components/education.tsx'
import { LorenzAttractorWrapper } from '~/components/lorenz-attractor.tsx'

type LoaderData = {
  blogRecommendations: Array<MdxListItem>
}

export const loader: LoaderFunction = async ({ request }) => {
  const timings = {}

  const data: LoaderData = {
    blogRecommendations: await getBlogRecommendations({ request, timings }),
  }
  return json(data, {
    headers: {
      'Cache-Control': 'private, max-age=3600',
      Vary: 'Cookie',
      'Server-Timing': getServerTimeHeader(timings),
    },
  })
}

export const headers: HeadersFunction = reuseUsefulLoaderHeaders

export const meta: MetaFunction<typeof loader, { root: RootLoaderType }> = ({
  matches,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const requestInfo = matches.find(m => m.id === 'root')?.data.requestInfo
  return getSocialMetas({
    title: 'Vinh Nguyen',
    description: 'Get to know Vinh Nguyen',
    keywords: 'vinh, vinh nguyen',
    url: getUrl(requestInfo),
    image: getSocialImageWithPreTitle({
      url: getDisplayUrl(requestInfo),
      featuredImage: 'vinh/avatar',
      preTitle: 'Get to know',
      title: `Vinh Nguyen`,
    }),
  })
}

function AboutIndex() {
  const { blogRecommendations } = useLoaderData<LoaderData>()

  return (
    <>
      <div className="w-screen h-screen fixed top-0 left-0 -z-50">
        <LorenzAttractorWrapper />
      </div>

      <HeroSection
        title={`fmt.Println("Hi, I am Vinh.")`}
        animatedTitle
        animatedTitleStyles="font-mono tracking-tighter"
        // imageBuilder={images.vinhAvatar}
      />

      <Grid className="mb-24 mt-16 lg:mb-48">
        <div className="col-span-full mb-12 lg:col-span-4 lg:mb-0">
          <Experiences />
          <Education />
        </div>

        <div className="col-span-full mb-8 lg:col-span-8 lg:mb-20 font-mono">
          <Paragraph className="mb-4">
            <span className="text-gradient-animated">&#47;&#47;</span> With over
            10 years of experience in software development, I&apos;ve immersed
            myself in a variety of projects across different industries. At my
            workplaces, we heavily rely on open-source software. Technologies
            like Node.js, Golang, Linux, Laravel, React.js, Vue.js, among
            others, form the backbone of our daily operations. My project
            portfolio spans diverse sectors including Education, Real Estate and
            Hospitality Rentals, Ride-hailing, Social Networking, Live
            Streaming, Banking, and more.
          </Paragraph>

          <div className="grid grid-cols-2 gap-4">
            <Paragraph>
              <span className="text-gradient-animated">&#47;&#47;</span> I
              thrive on challenges, seeking them out both professionally and
              personally. Being a software engineer provides me with the perfect
              platform to embrace and conquer these experiences.
            </Paragraph>

            <Paragraph>
              <span className="text-gradient-animated">&#47;&#47;</span> In my
              free time, I write technical articles to share knowledge and help
              me understand things better. Writing these articles helps me
              navigate the challenges of being a developer. I love physics,
              especially astrophysics and astronomy, and the universe amazes me.
              Besides work, I am also a husband and father.
            </Paragraph>
          </div>
        </div>
      </Grid>

      <BlogSection
        articles={blogRecommendations}
        title="Suggested Readings"
        description=""
      />
    </>
  )
}

export default AboutIndex
